import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import DefaultModal from '../organisms/DefaultModal';
import styled from 'styled-components';
import PersonalData from '../organisms/PersonalData';
import PersonalDocuments from '../organisms/PersonalDocuments';
import ProfessionalDocuments from '../organisms/ProfessionalDocuments';
import BankDetails from '../organisms/BankDetails';
import Dependents from '../organisms/Dependents';
import Address from '../organisms/Address';
import StepCombined from '../molecules/StepCombined';
import UploadDocuments from '../organisms/UploadDocuments';
import FinalizationConfirmation from '../organisms/FinalizationConfirmation';
import { FormInstance } from 'antd/lib/form';
import { useNavigate } from 'react-router-dom';
import admissionRepository from '../../services/repository/admission';
import { AdmissionData } from '../../interfaces/AdmissionData';
import { plainToInstance } from 'class-transformer';
import { Admission } from '../../types/Admission';
import axios from 'axios';
import uploadRepo from '../../services/repository/upload';
import StepManager from '../../services/util/step-manager/StepManager';
import { Events } from '../../constants/Events';
import { initialRules, updateRules } from '../../services/util/field-rules-manager/FieldRuleService';
import { UpdatedAdmissionRulesDetail } from '../../services/util/field-rules-manager/interfaces/fieldRuleInterfaces';
import { StepEnum } from '../../services/util/step-manager/enum/StepEnum';
import usersRepository from '../../services/repository/users';
import AdmissionMetadata from '../../types/AdmissionMetadata';
import dayjs from 'dayjs';
import { useAuth } from '../../services/context/AuthContext';

const Wrapper = styled.div`
    padding: 32px;
`;

type FormRefs = {
  [key: string]: React.MutableRefObject<FormInstance | null>;
};

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(StepManager.getFirstStep());
  const [admissionDataRules, setAdmissionDataRules] = useState(initialRules);
  const [admissionMetadata, setAdmissionMetadata] = useState({
    id: '',
    name: '',
    email: '',
    cpf: '',
    createdAt: dayjs(),
    professionalIdentification: 0,
  });
  const formRefs: FormRefs = {
    [StepEnum.PERSONAL_DATA]: useRef(null),
    [StepEnum.ADDRESS]: useRef(null),
    [StepEnum.PERSONAL_DOCUMENTS]: useRef(null),
    [StepEnum.PROFESSIONAL_DOCUMENTS]: useRef(null),
    [StepEnum.BANK_DETAILS]: useRef(null),
    [StepEnum.DEPENDENTS]: useRef(null),
    [StepEnum.DOCUMENTS_UPLOADS]: useRef(null),
    [StepEnum.FINALIZATION]: useRef(null),
  };

  const [cache, setCache] = useState<AdmissionData>({
    personal: {},
    address: {},
    personalDocuments: {},
    professionalDocuments: {},
    banking: {},
    additionalBankingData: {},
    dependents: [],
    step: currentStep.id,
  });

  const [formDataFinal, setFormDataFinal] = useState<AdmissionData>({
    personal: {},
    address: {},
    personalDocuments: {},
    professionalDocuments: {},
    banking: {},
    additionalBankingData: {},
    dependents: [],
    step: currentStep.id,
  });

  const [documents, setDocuments] = useState([]);

  const findAllDocuments = async () => {
    const response = await uploadRepo.documents();
    setDocuments(response.data);
  };

  useEffect(() => {
    if (currentStep.id === StepEnum.DOCUMENTS_UPLOADS) {
      findAllDocuments();
    }
  }, [currentStep]);

  const refreshCache = async () => {
    const admissionMetadataResult = await usersRepository.getCurrentUserAdmissionMetaData();
    try {
      setAdmissionMetadata(admissionMetadataResult);

      const response = await admissionRepository.getAdmissionData();
      const admissionData: AdmissionData = plainToInstance(Admission, response.data);
      syncRulesAndFormData(admissionData, admissionMetadataResult);
      setFormDataFinal(admissionData);

      if (admissionData.step) {
        const step = StepManager.getStepById(admissionData.step);
        if (step) {
          setCurrentStep(step);
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //when user has no data it will return 404
        if (error?.response?.status === 404) {
          const metadataInitialValues = getInitialValuesFromMetadata(admissionMetadataResult);
          syncRulesAndFormData(metadataInitialValues, admissionMetadataResult);
          return;
        }
      }
    }
  };

  const getInitialValuesFromMetadata = (admissionMetaData : AdmissionMetadata) => {
    const isCellPhoneNumber = admissionMetaData.phone && admissionMetaData.phone.startsWith('9');

    const initialAdmissionData: AdmissionData = {
      personal: {
        data: {
          cpf: admissionMetaData.cpf,
          dateOfBirth: dayjs(admissionMetaData.birthDate),
        },
        nameAffiliation: {
          name: admissionMetaData.name,
          socialName: admissionMetaData.socialName,
        },
        information: {email : admissionMetaData.email},
        contact: {
          dddCellPhone: isCellPhoneNumber && admissionMetaData.dddPhone || undefined,
          cellphone: isCellPhoneNumber && admissionMetaData.phone || undefined,
          dddLandLine: !isCellPhoneNumber && admissionMetaData.dddPhone || undefined,
          landline: !isCellPhoneNumber && admissionMetaData.phone || undefined,
        },
      },
      address: {},
      personalDocuments: {},
      professionalDocuments: {},
      banking: {},
      additionalBankingData: {},
      dependents: [],
      step: currentStep.id,
    };

    return initialAdmissionData;
  };

  const handleFormRulesUpdateEvent = () => {
    getCompleteAdmissionData().then(admissionData => {
      syncRulesAndFormData(admissionData, admissionMetadata);
    });
  };

  /**
   * Returns the complete admission Data
   */
  const getCompleteAdmissionData = async (): Promise<AdmissionData> => {
    const currentFormRef = getCurrentFormInstance();

    let currentStepValues: any = await currentFormRef.getFieldsValue();


    if (currentStep.id === StepEnum.DEPENDENTS) {
      currentStepValues = currentStepValues.items;
    }

    return { ...formDataFinal, [currentStep.id]: currentStepValues, step: currentStep.id };
  };

  const getCurrentFormInstance = () => {
    const currentFormRef = formRefs[currentStep.id]?.current;
    if (!currentFormRef) {
      const error = new Error('step not found, contact the system administrator');
      console.error(error);
      throw error;
    }

    return currentFormRef;
  };

  const syncRulesAndFormData = (admissionFormData: AdmissionData, admissionMetadata: AdmissionMetadata) => {
    const updatedData: UpdatedAdmissionRulesDetail = updateRules(admissionFormData, admissionMetadata);
    setCache(updatedData.updatedFormData);
    setAdmissionDataRules(updatedData.updatedFormRules);
  };

  useEffect(() => {
    refreshCache();
    findAllDocuments();
  }, []);

  useEffect(() => {
    window.addEventListener(Events.FORM_RULES.UPDATE, handleFormRulesUpdateEvent as EventListener);
    return () => {
      window.removeEventListener(Events.FORM_RULES.UPDATE, handleFormRulesUpdateEvent as EventListener);
    };
  }, [currentStep]);


  const handleNext = async () => {
    try {
      await getCurrentFormInstance().validateFields();
      const admissionData = await getCompleteAdmissionData();
      const nextStep = StepManager.getNextStep(currentStep);
      const finalValues = { ...admissionData, step: nextStep?.id || currentStep.id };
      setFormDataFinal(finalValues);
      await admissionRepository.sendAdmission(finalValues);
      setCache(finalValues);

      if (nextStep)
        setCurrentStep(nextStep);
    } catch (e) {
      message.error('Erro de validação. Confira o preenchimento dos campos.');
    }
  };

  const handleAdmissionFinalization = async () => {
    const lastStep = StepManager.getLastStep();
    await admissionRepository.sendAdmission({ ...formDataFinal, step: lastStep.id });
    setCurrentStep(lastStep);
  };

  const handleStepClick = (selectedStepIndex: number) => {
    const step = StepManager.getStepByIndex(selectedStepIndex);
    if (step && selectedStepIndex <= currentStep.order && currentStep.id !== StepEnum.FINALIZATION) {
      setCurrentStep(step);
    }
  };

  const { logout } = useAuth();

  return (
    <DefaultModal width={750} showBtnExit={true} squareWidth={'35%'} onLogout={() => {
      navigate('/');
      logout();
    }}>
      <Wrapper>
        {(currentStep.id !== StepEnum.FINALIZATION && (
          <StepCombined currentStep={currentStep} onChangeStep={handleStepClick} />
        ))}

        {currentStep.id === StepManager.enum.PERSONAL_DATA && (
          <PersonalData ref={formRefs[StepEnum.PERSONAL_DATA]} handleNext={handleNext} initForm={cache.personal}
            admissionDataRules={admissionDataRules} />
        )}

        {currentStep.id === StepManager.enum.ADDRESS && (
          <Address ref={formRefs[StepEnum.ADDRESS]} handleNext={handleNext} initForm={cache.address}
            admissionDataRules={admissionDataRules} />
        )}

        {currentStep.id === StepManager.enum.PERSONAL_DOCUMENTS && (
          <PersonalDocuments ref={formRefs[StepEnum.PERSONAL_DOCUMENTS]} handleNext={handleNext}
            admissionDataRules={admissionDataRules}
            initForm={cache.personalDocuments} />
        )}

        {currentStep.id === StepManager.enum.PROFESSIONAL_DOCUMENTS && (
          <ProfessionalDocuments ref={formRefs[StepEnum.PROFESSIONAL_DOCUMENTS]} handleNext={handleNext}
            admissionDataRules={admissionDataRules}
            initForm={cache.professionalDocuments} />
        )}

        {currentStep.id === StepManager.enum.BANK_DETAILS && (
          <BankDetails ref={formRefs[StepEnum.BANK_DETAILS]}
            handleNext={handleNext}
            admissionDataRules={admissionDataRules}
            admissionMetadata={admissionMetadata}
            initForm={{ ...cache.banking }}
          />
        )}

        {currentStep.id === StepManager.enum.DEPENDENTS && (
          <Dependents ref={formRefs[StepEnum.DEPENDENTS]} handleNext={handleNext} initForm={cache.dependents} />
        )}

        {currentStep.id === StepManager.enum.DOCUMENTS_UPLOADS && (
          <UploadDocuments ref={formRefs[StepEnum.DOCUMENTS_UPLOADS]} handleNext={handleNext} files={documents} />
        )}

        {(currentStep.id === StepManager.enum.FINALIZATION_CONFIRMATION || currentStep.id === StepManager.enum.FINALIZATION) && (
          <FinalizationConfirmation handleAdmissionFinalization={handleAdmissionFinalization}
            currentStep={currentStep} />
        )}

      </Wrapper>
    </DefaultModal>
  );
};

export default Register;
